import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { KeyRound, AlertCircle, CheckCircle2 } from "lucide-react";

export default function ChangePassword() {
  const { user, changePassword, logout } = useAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const forced = !!user?.must_change_password;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (next.length < 8) {
      setError("New password must be at least 8 characters.");
      return;
    }
    if (next !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setSubmitting(true);
    const res = await changePassword(current, next);
    setSubmitting(false);
    if (!res.ok) {
      setError(res.error);
    } else {
      navigate("/dashboard", { replace: true });
    }
  };

  return (
    <div className="min-h-screen bg-bg bg-grid flex items-start justify-start px-6 md:px-16 py-16" data-testid="change-password-page">
      <form
        onSubmit={onSubmit}
        className="w-full max-w-md"
        data-testid="change-password-form"
      >
        <div className="text-[11px] font-mono uppercase tracking-widest text-ink-muted mb-2">
          Security
        </div>
        <h1 className="text-2xl font-semibold tracking-tight text-ink mb-2">
          {forced ? "Set your new password" : "Change password"}
        </h1>
        {forced && (
          <div
            className="mb-6 flex items-start gap-2 border rounded-sm px-3 py-2 text-xs"
            style={{
              backgroundColor: "rgba(244, 184, 96, 0.06)",
              borderColor: "rgba(244, 184, 96, 0.3)",
              color: "#F4B860",
            }}
          >
            <CheckCircle2 size={14} className="shrink-0 mt-0.5" />
            <span>
              You&rsquo;re signed in as <span className="font-medium">{user?.email}</span>.
              Please replace the temporary password before you continue.
            </span>
          </div>
        )}

        <label className="block mb-4">
          <div className="text-[11px] font-mono uppercase tracking-wider text-ink-muted mb-1.5">
            Current password
          </div>
          <input
            type="password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            required
            data-testid="current-password-input"
            className="w-full bg-bg border border-line rounded-sm px-3 py-2 text-sm text-ink focus:border-cyan transition-colors"
          />
        </label>

        <label className="block mb-4">
          <div className="text-[11px] font-mono uppercase tracking-wider text-ink-muted mb-1.5">
            New password
          </div>
          <input
            type="password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            required
            minLength={8}
            data-testid="new-password-input"
            className="w-full bg-bg border border-line rounded-sm px-3 py-2 text-sm text-ink focus:border-cyan transition-colors"
          />
          <div className="text-[11px] text-ink-muted mt-1">Minimum 8 characters.</div>
        </label>

        <label className="block mb-6">
          <div className="text-[11px] font-mono uppercase tracking-wider text-ink-muted mb-1.5">
            Confirm new password
          </div>
          <input
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
            data-testid="confirm-password-input"
            className="w-full bg-bg border border-line rounded-sm px-3 py-2 text-sm text-ink focus:border-cyan transition-colors"
          />
        </label>

        {error && (
          <div
            className="mb-4 flex items-start gap-2 border rounded-sm px-3 py-2 text-xs"
            style={{
              backgroundColor: "rgba(239, 68, 68, 0.06)",
              borderColor: "rgba(239, 68, 68, 0.3)",
              color: "#F87171",
            }}
            data-testid="change-password-error"
          >
            <AlertCircle size={14} className="shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={submitting}
            data-testid="change-password-submit-btn"
            className="flex items-center gap-2 bg-cyan text-bg font-medium py-2 px-4 rounded-sm hover:bg-cyan-hover transition-colors disabled:opacity-60 active:scale-[0.98]"
          >
            <KeyRound size={14} />
            {submitting ? "Saving…" : "Update password"}
          </button>
          {!forced && (
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="text-ink-secondary text-sm hover:text-ink transition-colors"
            >
              Cancel
            </button>
          )}
          {forced && (
            <button
              type="button"
              onClick={async () => { await logout(); navigate("/login", { replace: true }); }}
              className="text-ink-secondary text-sm hover:text-ink transition-colors"
              data-testid="cancel-signout-btn"
            >
              Sign out
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
